import { isEmpty, isNil } from 'lodash';
import type {
  ApiResponse,
  FileDataType,
  FileDetails,
  FileResponse,
  IterationDataset,
  IterationDatasetResponse,
  OrganisationLabel,
} from '..';
import { apiDataResponseMapper } from '..';
import { Converter, CustomDate } from '../../utils';
import type {
  AnalyticsRequestPayload,
  CapturedDsmCogInfo,
  CapturedDsmCogInfoResponse,
  ElevationProfileData,
  ElevationProfileResponse,
  GenerateElevationProfilePayload,
  GenerateElevationProfilePayloadMapper,
  Iteration,
  IterationData,
  IterationLabelData,
  IterationList,
  IterationListData,
  IterationListItem,
} from './types';

export const iterationListMapper = (
  payload: IterationListData,
): IterationList => {
  return {
    list: payload.iterations.map((iteration) => {
      const mappedData: IterationListItem = apiDataResponseMapper(iteration);
      mappedData.date = new CustomDate(mappedData.date);

      if (!isEmpty(iteration.captured_dsm)) {
        mappedData.capturedDsm = apiDataResponseMapper<
          FileResponse,
          FileDataType
        >(iteration.captured_dsm);
      }

      if (!isEmpty(iteration.captured_dsm_cog)) {
        mappedData.capturedDsmCog = apiDataResponseMapper<
          CapturedDsmCogInfoResponse,
          CapturedDsmCogInfo
        >(iteration.captured_dsm_cog);
      }

      return mappedData;
    }),
    total: payload.total,
    siteName: payload.site_name,
    projectId: payload.project_id,
    canManageIterations: payload.can_manage_iterations,
    pageNumber: payload.page_number,
    requestedIterationId: payload.requested_iteration_id || null,
    accessType: payload.access_type,
  };
};

export const iterationResponseMapper = (
  response: ApiResponse<IterationData>,
): Iteration => {
  const capturedDSM = !isNil(response.data.captured_dsm)
    ? apiDataResponseMapper<FileResponse, FileDataType>(response.data.captured_dsm)
    : null;

  const capturedDSMCog = !isNil(response.data.captured_dsm_cog)
    ? apiDataResponseMapper<FileResponse, FileDataType>(
        response.data.captured_dsm_cog,
      )
    : null;

  const mappedLabel = !isNil(response.data.label)
    ? apiDataResponseMapper<IterationLabelData, OrganisationLabel>(
        response.data.label,
      )
    : null;

  const mappedData: Iteration = {
    id: response.data.id,
    name: response.data.name,
    date: new Date(response.data.date),
    info: response.data.info,
    label: mappedLabel,
    siteId: response.data.site,
    capturedDSMCog,
    capturedDSM,
    accessTags: response.data.access_tags ?? [],
  };

  const { bytesToSizeConverter } = Converter;

  if (response.data.orthomosaic) {
    mappedData['orthomosaic'] = {
      id: response.data.orthomosaic.id,
      status: response.data.orthomosaic.status as unknown as FileDetails['status'],
      name: response.data.orthomosaic.filename,
      size: bytesToSizeConverter(response.data.orthomosaic.size),
      downloadUrl: response.data.orthomosaic.download_url,
    };
  }

  if (response.data.iteration_dataset) {
    const iterationDataset = apiDataResponseMapper<
      IterationDatasetResponse,
      IterationDataset
    >(response.data.iteration_dataset);

    mappedData['iterationDataset'] = iterationDataset;
  }

  return mappedData;
};

export const elevationProfilePayloadMapper = (
  payload: GenerateElevationProfilePayload,
): GenerateElevationProfilePayloadMapper => {
  const { lineWkt, ...rest } = payload;
  return {
    ...rest,
    line_wkt: lineWkt,
  };
};

export const elevationProfileResponseMapper = (
  payload: ElevationProfileResponse,
): ElevationProfileData => {
  const { elevation_profiles, distance_points, ...rest } = payload;
  return {
    elevationProfiles: elevation_profiles,
    distancePoints: distance_points,
    ...rest,
  };
};

export const analyticsRequestPayloadMapper = (
  payload: AnalyticsRequestPayload,
) => {
  return {
    polygon_wkt: payload.polygonWkt,
    selected_outputs: payload.selectedOutputs,
  };
};
